import { useEffect, useRef, type RefObject } from 'react';

/**
 * Offer-impression viewport producer (V2-017). Fires `onInView(resultId)` exactly
 * once per result id the first time `ref`'s element intersects the viewport, then
 * disconnects. Narrowly scoped to the single offer_impression conversion event —
 * NOT a general analytics/event abstraction.
 *
 * Invoke from the durable ResultPage (not the transient ReportCta) so the fired
 * history survives ReportCta unmount/remount. `firedResultIdsRef` retains EVERY id
 * fired during the hook's mount: A → loading → A yields one request for A; A → B → A
 * yields two (one A, one B) and never a third.
 *
 * Stale-callback safety: the observer captures its own `resultId`, passes it to the
 * producer, and — before firing — re-checks the LATEST render state via refs (still
 * active, still enabled, current id still equals the captured id, not already fired,
 * at least one entry intersecting). A callback delivered after cleanup, during a
 * loading/disabled window, or after the result changed does nothing and does NOT
 * mark the captured id fired. `onInView` is read through a ref so a changing callback
 * identity alone never re-creates the observer.
 *
 * No-op when `enabled` is false, when the element is absent, or when the environment
 * has no IntersectionObserver (e.g. jsdom without a test double).
 */
export function useOfferImpressionOnView(
  ref: RefObject<Element | null>,
  enabled: boolean,
  resultId: string,
  onInView: (observedResultId: string) => void,
): void {
  const firedResultIdsRef = useRef(new Set<string>());
  const onInViewRef = useRef(onInView);
  onInViewRef.current = onInView;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const currentResultIdRef = useRef(resultId);
  currentResultIdRef.current = resultId;

  useEffect(() => {
    if (!enabled) return;
    if (firedResultIdsRef.current.has(resultId)) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') return;

    let active = true;

    const observer = new IntersectionObserver((entries) => {
      // Ignore callbacks delivered after cleanup or once eligibility/identity has
      // moved on. Every check reads the LATEST render state via refs, so a stale
      // observer for a previous result can neither send nor mark that id fired.
      if (!active) return;
      if (!enabledRef.current) return;
      if (currentResultIdRef.current !== resultId) return;
      if (firedResultIdsRef.current.has(resultId)) return;
      if (!entries.some((entry) => entry.isIntersecting)) return;
      // Mark fired immediately before invoking the producer, after all checks pass.
      firedResultIdsRef.current.add(resultId);
      observer.disconnect();
      onInViewRef.current(resultId);
    });
    observer.observe(el);

    return () => {
      active = false;
      observer.disconnect();
    };
  }, [ref, enabled, resultId]);
}
